/**
 * Viewport-limited visible-UI snapshot for Ask Chckvet.
 *
 * Not a screenshot and not innerHTML. Walks the main content pane plus
 * open dialogs/toasts, keeps headings / alerts / selected tabs / disabled
 * controls / switch-checkbox-radio state / table shapes, and caps the
 * result so a client list cannot dump into the prompt.
 */

import { ASK_CHCKVET_DOCK_ATTR, ASK_CHCKVET_MAIN_ATTR } from './ask-chckvet-dom'

/** Stay well under the server clamp (8000). */
export const ASK_CHCKVET_VISIBLE_TEXT_MAX_CHARS = 4000

const MAX_LINES = 150
const MAX_DISABLED_CONTROLS = 50
const MAX_TABLE_HEADERS = 50
const MAX_TOGGLES = 50

const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi
const UUID_RE =
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi

const SKIP_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'NOSCRIPT',
    'SVG',
    'PATH',
    'CANVAS',
    'IFRAME',
])

export function captureVisibleUiSnapshot(): string | undefined {
    if (typeof document === 'undefined') return undefined

    try {
        const lines: string[] = []
        const main = document.querySelector(`[${ASK_CHCKVET_MAIN_ATTR}]`)
        if (main instanceof HTMLElement) {
            collectFromRoot(main, main, lines)
        }

        for (const dialog of document.querySelectorAll(
            '[role="dialog"], [role="alertdialog"]',
        )) {
            if (!(dialog instanceof HTMLElement)) continue
            if (isInsideDock(dialog)) continue
            if (main?.contains(dialog)) continue
            if (!isDisplayed(dialog)) continue
            const title = accessibleName(dialog) || 'Dialog'
            lines.push(`Open dialog: ${title}`)
            collectFromRoot(dialog, dialog, lines)
        }

        for (const alert of document.querySelectorAll(
            '[role="alert"], [role="status"]',
        )) {
            if (!(alert instanceof HTMLElement)) continue
            if (isInsideDock(alert)) continue
            if (main?.contains(alert)) continue
            if (alert.closest('[role="dialog"], [role="alertdialog"]')) {
                continue
            }
            if (!isDisplayed(alert)) continue
            const text = collapseWhitespace(alert.innerText)
            if (text) lines.push(`Alert: ${text}`)
        }

        return finalizeSnapshot(lines)
    } catch {
        return undefined
    }
}

function collectFromRoot(
    root: HTMLElement,
    viewport: HTMLElement,
    lines: string[],
): void {
    collectToggleStates(root, viewport, lines)
    visit(root, viewport, lines, { disabledEmitted: 0 })
}

/**
 * Chakra Switch/Checkbox/Radio hide the native input, so the tree walk
 * never sees them as visible text. Query them directly and use the
 * visible widget (track / control) for viewport + a nearby label.
 */
function collectToggleStates(
    root: HTMLElement,
    viewport: HTMLElement,
    lines: string[],
): void {
    let emitted = 0

    for (const el of root.querySelectorAll(
        'input[type="checkbox"], input[type="radio"]',
    )) {
        if (!(el instanceof HTMLElement)) continue
        if (emitted >= MAX_TOGGLES || lines.length >= MAX_LINES) return
        if (shouldSkipToggle(el)) continue
        if (!toggleIsInView(el, viewport)) continue
        const summary = summarizeToggle(el)
        if (!summary) continue
        lines.push(summary)
        emitted += 1
    }

    for (const el of root.querySelectorAll(
        '[role="switch"], [role="checkbox"], [role="radio"]',
    )) {
        if (!(el instanceof HTMLElement)) continue
        if (emitted >= MAX_TOGGLES || lines.length >= MAX_LINES) return
        if (el.matches('input')) continue
        if (el.querySelector('input[type="checkbox"], input[type="radio"]')) {
            continue
        }
        if (shouldSkipToggle(el)) continue
        if (!toggleIsInView(el, viewport)) continue
        const summary = summarizeToggle(el)
        if (!summary) continue
        lines.push(summary)
        emitted += 1
    }
}

function shouldSkipToggle(el: HTMLElement): boolean {
    if (isInsideDock(el)) return true
    if (el.closest('table, [role="table"], [role="grid"]')) return true
    return false
}

function visualToggleTarget(el: HTMLElement): HTMLElement {
    const widget = el.closest('.chakra-switch, .chakra-checkbox, .chakra-radio')
    if (widget instanceof HTMLElement) return widget
    const label = el.closest('label')
    if (label instanceof HTMLElement) return label
    return el
}

function toggleIsInView(el: HTMLElement, viewport: HTMLElement): boolean {
    const visual = visualToggleTarget(el)
    if (isDisplayed(visual) && intersectsViewport(visual, viewport)) return true
    // Native inputs that aren't visually hidden (rare in this app).
    return isDisplayed(el) && intersectsViewport(el, viewport)
}

function summarizeToggle(el: HTMLElement): string | undefined {
    const checked = toggleChecked(el)
    if (checked === undefined) return undefined
    const kind = toggleKind(el)
    const label = labelForToggle(el)
    const disabled =
        el.hasAttribute('disabled') ||
        el.getAttribute('aria-disabled') === 'true' ||
        visualToggleTarget(el).getAttribute('data-disabled') === '' ||
        visualToggleTarget(el).getAttribute('data-disabled') === 'true'
    const state = checked ? 'on' : 'off'
    const suffix = disabled ? ' (disabled)' : ''
    return `${kind} ${label}: ${state}${suffix}`
}

function toggleChecked(el: HTMLElement): boolean | undefined {
    if (el instanceof HTMLInputElement) return el.checked
    const aria = el.getAttribute('aria-checked')
    if (aria === 'true') return true
    if (aria === 'false') return false
    return undefined
}

function toggleKind(el: HTMLElement): 'Switch' | 'Checkbox' | 'Radio' {
    if (el.closest('.chakra-switch') || el.getAttribute('role') === 'switch') {
        return 'Switch'
    }
    if (
        (el instanceof HTMLInputElement && el.type === 'radio') ||
        el.getAttribute('role') === 'radio' ||
        el.closest('.chakra-radio')
    ) {
        return 'Radio'
    }
    return 'Checkbox'
}

function labelForToggle(el: HTMLElement): string {
    const labelled = accessibleName(el)
    if (labelled) return labelled

    if (el instanceof HTMLInputElement && el.labels?.length) {
        const fromLabel = collapseWhitespace(el.labels[0].innerText)
        // Chakra Switch's wrapping <label> is just the track — empty text.
        if (fromLabel && fromLabel.length <= 100) return fromLabel
    }

    const wrap = el.closest('label')
    if (
        wrap instanceof HTMLElement &&
        !wrap.classList.contains('chakra-switch')
    ) {
        const fromWrap = collapseWhitespace(wrap.innerText)
        if (fromWrap && fromWrap.length <= 100) return fromWrap
    }

    const widget = el.closest('.chakra-switch, .chakra-checkbox, .chakra-radio')
    let node: HTMLElement | null =
        widget instanceof HTMLElement ? widget.parentElement : el.parentElement
    for (let depth = 0; depth < 6 && node; depth++) {
        const clone = node.cloneNode(true) as HTMLElement
        clone
            .querySelectorAll('.chakra-switch, .chakra-checkbox, .chakra-radio')
            .forEach(part => part.remove())
        const text = collapseWhitespace(clone.innerText).replace(
            /\s+(Yes|No)$/i,
            '',
        )
        if (text.length >= 2 && text.length <= 100) return text
        node = node.parentElement
    }

    return toggleKind(el)
}

function visit(
    el: Element,
    viewport: HTMLElement,
    lines: string[],
    state: { disabledEmitted: number },
): void {
    if (lines.length >= MAX_LINES) return
    if (!(el instanceof HTMLElement)) return
    if (SKIP_TAGS.has(el.tagName)) return
    if (isInsideDock(el)) return
    if (!isDisplayed(el)) return
    if (!intersectsViewport(el, viewport)) return

    if (isDataTable(el)) {
        lines.push(summarizeTable(el))
        return
    }

    if (isHeading(el)) {
        const text = collapseWhitespace(el.innerText)
        if (text) lines.push(`Heading: ${text}`)
        return
    }

    if (isAlert(el)) {
        const text = collapseWhitespace(el.innerText)
        if (text) lines.push(`Alert: ${text}`)
        return
    }

    if (isSelectedTab(el)) {
        const text = accessibleName(el) || collapseWhitespace(el.innerText)
        if (text) lines.push(`Selected tab: ${text}`)
        return
    }

    if (isControl(el)) {
        const summary = summarizeControl(el)
        if (summary) {
            if (summary.startsWith('Disabled')) {
                if (state.disabledEmitted < MAX_DISABLED_CONTROLS) {
                    lines.push(summary)
                    state.disabledEmitted += 1
                }
            } else if (
                el.tagName === 'SELECT' ||
                el.tagName === 'INPUT' ||
                el.tagName === 'TEXTAREA'
            ) {
                lines.push(summary)
            }
        }
        if (el.tagName === 'BUTTON' || el.getAttribute('role') === 'button') {
            return
        }
        if (
            el.tagName === 'INPUT' ||
            el.tagName === 'SELECT' ||
            el.tagName === 'TEXTAREA'
        ) {
            return
        }
    }

    const children = Array.from(el.children)
    if (children.length === 0) {
        const text = collapseWhitespace(el.innerText)
        if (shouldKeepLooseText(text)) lines.push(text)
        return
    }

    if (isPhrasingContainer(el, children)) {
        const text = collapseWhitespace(el.innerText)
        if (shouldKeepLooseText(text)) lines.push(text)
        return
    }

    for (const child of children) {
        if (lines.length >= MAX_LINES) return
        visit(child, viewport, lines, state)
    }
}

function isDataTable(el: Element): boolean {
    if (el.tagName === 'TABLE') return true
    const role = el.getAttribute('role')
    return role === 'table' || role === 'grid' || role === 'treegrid'
}

function summarizeTable(el: Element): string {
    const headers = [...el.querySelectorAll('th, [role="columnheader"]')]
        .map(header => collapseWhitespace(header.textContent))
        .filter(Boolean)
        .slice(0, MAX_TABLE_HEADERS)
    const rowCount = countTableRows(el)
    const headerPart = headers.length ? headers.join(', ') : 'columns unknown'
    return `Table (${headerPart}): ${rowCount} row(s)`
}

function countTableRows(el: Element): number {
    const bodyRows = el.querySelectorAll('tbody tr').length
    if (bodyRows > 0) return bodyRows
    const roleRows = [...el.querySelectorAll('[role="row"]')].filter(
        row => !row.querySelector('[role="columnheader"]'),
    ).length
    if (roleRows > 0) return roleRows
    return el.querySelectorAll('tr').length
}

function isHeading(el: Element): boolean {
    if (/^H[1-6]$/.test(el.tagName)) return true
    return el.getAttribute('role') === 'heading'
}

function isAlert(el: Element): boolean {
    const role = el.getAttribute('role')
    return (
        role === 'alert' ||
        role === 'status' ||
        el.classList.contains('chakra-alert')
    )
}

function isSelectedTab(el: Element): boolean {
    return (
        el.getAttribute('aria-selected') === 'true' ||
        el.getAttribute('aria-current') === 'page' ||
        el.getAttribute('aria-current') === 'true'
    )
}

function isControl(el: Element): boolean {
    if (
        el.tagName === 'BUTTON' ||
        el.tagName === 'INPUT' ||
        el.tagName === 'SELECT' ||
        el.tagName === 'TEXTAREA'
    ) {
        return true
    }
    const role = el.getAttribute('role')
    return (
        role === 'button' ||
        role === 'tab' ||
        role === 'switch' ||
        role === 'checkbox' ||
        role === 'radio'
    )
}

function summarizeControl(el: HTMLElement): string | undefined {
    const name = accessibleName(el) || collapseWhitespace(el.innerText)
    const disabled =
        el.hasAttribute('disabled') ||
        el.getAttribute('aria-disabled') === 'true'

    if (el.tagName === 'SELECT') {
        const select = el as HTMLSelectElement
        const selected = collapseWhitespace(
            select.selectedOptions[0]?.text || select.value,
        )
        const label = name || select.name || 'Select'
        return `Select ${label}: ${selected || 'empty'}`
    }

    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
        const input = el as HTMLInputElement | HTMLTextAreaElement
        const label =
            name ||
            input.getAttribute('placeholder') ||
            input.name ||
            input.tagName
        if (input instanceof HTMLInputElement && input.type === 'password') {
            return `Input ${label}: [hidden]`
        }
        if (
            input instanceof HTMLInputElement &&
            (input.type === 'checkbox' || input.type === 'radio')
        ) {
            // Handled in collectToggleStates so visually-hidden Chakra
            // inputs still get an on/off line with a nearby label.
            return undefined
        }
        const value = collapseWhitespace(input.value)
        if (!value) return `Input ${label}: empty`
        return `Input ${label}: ${value.slice(0, 80)}`
    }

    if (!disabled) return undefined
    if (!name) return undefined
    return `Disabled: ${name}`
}

function isPhrasingContainer(el: HTMLElement, children: Element[]): boolean {
    if (el.tagName === 'P' || el.tagName === 'LABEL' || el.tagName === 'LI') {
        return true
    }
    return children.every(
        child =>
            child instanceof HTMLElement &&
            (child.tagName === 'SPAN' ||
                child.tagName === 'STRONG' ||
                child.tagName === 'EM' ||
                child.tagName === 'B' ||
                child.tagName === 'I' ||
                child.tagName === 'SMALL' ||
                child.tagName === 'A'),
    )
}

function shouldKeepLooseText(text: string): boolean {
    if (text.length < 8) return false
    if (text.length > 400) return false
    return true
}

function isInsideDock(el: Element): boolean {
    if (el.closest(`[${ASK_CHCKVET_DOCK_ATTR}]`)) return true
    if (el.closest('#intercom-container, .intercom-lightweight-app')) {
        return true
    }
    return false
}

function isDisplayed(el: Element): boolean {
    if (!(el instanceof HTMLElement)) return false
    if (el.hidden) return false
    if (el.getAttribute('aria-hidden') === 'true') return false
    const style = window.getComputedStyle(el)
    if (style.display === 'none' || style.visibility === 'hidden') return false
    if (style.visibility === 'collapse') return false
    return true
}

function intersectsViewport(el: Element, viewport: HTMLElement): boolean {
    const root = viewport.getBoundingClientRect()
    const box = el.getBoundingClientRect()
    if (box.width === 0 && box.height === 0) return false
    return (
        box.bottom > root.top &&
        box.top < root.bottom &&
        box.right > root.left &&
        box.left < root.right
    )
}

function accessibleName(el: Element): string {
    const labelledBy = el.getAttribute('aria-labelledby')
    if (labelledBy) {
        const parts = labelledBy
            .split(/\s+/)
            .map(id => document.getElementById(id)?.textContent)
            .map(text => collapseWhitespace(text))
            .filter(Boolean)
        if (parts.length) return parts.join(' ')
    }
    return collapseWhitespace(
        el.getAttribute('aria-label') || el.getAttribute('title') || '',
    )
}

function collapseWhitespace(raw: string | null | undefined): string {
    return (raw ?? '').replace(/\s+/g, ' ').trim()
}

function redact(raw: string): string {
    return raw.replace(EMAIL_RE, '[email]').replace(UUID_RE, '[id]')
}

function finalizeSnapshot(lines: string[]): string | undefined {
    const seen = new Set<string>()
    const unique: string[] = []
    for (const line of lines) {
        const redacted = redact(line)
        if (!redacted || seen.has(redacted)) continue
        seen.add(redacted)
        unique.push(redacted)
        if (unique.length >= MAX_LINES) break
    }

    if (unique.length === 0) return undefined

    let text = unique.join('\n')
    if (text.length <= ASK_CHCKVET_VISIBLE_TEXT_MAX_CHARS) return text
    return `${text.slice(0, ASK_CHCKVET_VISIBLE_TEXT_MAX_CHARS)}\n[truncated]`
}
